import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { TileSkeletonLoader } from './TileSkeletonLoader';
import { cn } from '@/lib/utils';

interface ComplianceCountTileProps {
  label: string;
  count: number;
  subtitle?: string;
  /** Slice 2 (BI): per-metric count colour (hex). Defaults to the primary text colour. */
  countColor?: string;
  /** Slice 2 (BI): use the cyan-toned border to flag a drill-through tile (e.g. Evidence Expiring). */
  accentBorder?: boolean;
  drillDownPath?: string;
  drillDownLabel?: string;
  isLoading: boolean;
  className?: string;
  'data-testid'?: string;
}

export function ComplianceCountTile({
  label,
  count,
  subtitle,
  countColor,
  accentBorder,
  drillDownPath,
  drillDownLabel = 'View details',
  isLoading,
  className,
  'data-testid': testId,
}: ComplianceCountTileProps) {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <Card className={cn('transition-shadow', className)}>
        <TileSkeletonLoader />
      </Card>
    );
  }

  return (
    <Card
      className={cn('transition-shadow hover:shadow-lg', accentBorder && 'border-cert-info-border', className)}
      data-testid={testId}
    >
      <CardContent className="p-4">
        <p className="text-sm text-text-secondary">{label}</p>
        <p
          className="mt-1 text-3xl font-semibold text-text-primary tabular-nums"
          style={countColor ? { color: countColor } : undefined}
        >
          {count}
        </p>
        {subtitle && (
          <p className="mt-0.5 text-xs text-text-secondary">{subtitle}</p>
        )}
        {drillDownPath && (
          <div className="mt-2">
            <button
              onClick={() => navigate(drillDownPath)}
              className="flex items-center gap-1 text-xs text-accent-cyan hover:text-text-primary transition-colors"
              data-testid="drilldown-link"
            >
              {drillDownLabel} <ArrowRight className="h-3 w-3" />
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
